import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Divider,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
} from '@mui/material';
import AuthContext from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';

export default function PurchaseKits() {
  const { user, tokens } = useContext(AuthContext);
  const { selectedPractice } = usePractice();
  const [kits, setKits] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [payer, setPayer] = useState('practice');
  const [alternatePayerDialogOpen, setAlternatePayerDialogOpen] = useState(false);
  const [alternatePayerStep, setAlternatePayerStep] = useState('contact');
  const [alternatePayerMethod, setAlternatePayerMethod] = useState('email');
  const [alternatePayerEmail, setAlternatePayerEmail] = useState('');
  const [alternatePayerPhone, setAlternatePayerPhone] = useState('');
  const [alternatePayerInvoiceUrl, setAlternatePayerInvoiceUrl] = useState('');
  const [alternatePayerQrFullscreen, setAlternatePayerQrFullscreen] = useState(false);
  const [alternatePayerSending, setAlternatePayerSending] = useState(false);
  const [alternatePayerError, setAlternatePayerError] = useState(null);
  const [selectedPracticeAddressKey, setSelectedPracticeAddressKey] = useState('');
  const [alternatePayerAddress, setAlternatePayerAddress] = useState({
    name: '',
    street_1: '',
    street_2: '',
    city: '',
    state: '',
    zip_code: '',
  });
  const qrFullscreenRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const fetchQuote = async () => {
      if (!selectedPractice?.id || !tokens?.id_token) {
        setQuote(null);
        return;
      }
      setQuoteLoading(true);
      setQuoteError(null);
      try {
        const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
        const res = await fetch(`${apiBase}/qb/purchase-kits/quote`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens.id_token}`,
          },
          body: JSON.stringify({ quantity: kits, practice_id: selectedPractice.id }),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Error ${res.status}: ${res.statusText}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setQuote(data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching quote:', err);
          setQuote(null);
          setQuoteError(err.message);
        }
      } finally {
        if (!cancelled) {
          setQuoteLoading(false);
        }
      }
    };
    fetchQuote();
    return () => {
      cancelled = true;
    };
  }, [kits, selectedPractice?.id, tokens?.id_token]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const isFs = Boolean(document.fullscreenElement);
      setAlternatePayerQrFullscreen(isFs);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  const handleOrderClick = () => {
    if (payer === 'patient') {
      setAlternatePayerDialogOpen(true);
    } else {
      setDialogOpen(true);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  const handleAlternatePayerDialogClose = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    setAlternatePayerDialogOpen(false);
    setAlternatePayerStep('contact');
    setAlternatePayerMethod('email');
    setAlternatePayerEmail('');
    setAlternatePayerPhone('');
    setAlternatePayerInvoiceUrl('');
    setAlternatePayerQrFullscreen(false);
    setAlternatePayerSending(false);
    setAlternatePayerError(null);
    setSelectedPracticeAddressKey('');
    setAlternatePayerAddress({
      name: '',
      street_1: '',
      street_2: '',
      city: '',
      state: '',
      zip_code: '',
    });
  };

  const handleCreateInvoice = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
      // Ensure a practice is selected
      if (!selectedPractice?.id) {
        throw new Error('No practice selected');
      }
      const res = await fetch(`${apiBase}/qb/purchase-kits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.id_token}`,
        },
        body: JSON.stringify({ quantity: kits, practice_id: selectedPractice.id }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
      const data = await res.json();
      // Close dialog, then navigate to Transactions highlighting this invoice
      setDialogOpen(false);
      navigate(`/transactions?realm_id=${encodeURIComponent(data.realm_id)}&invoice_id=${encodeURIComponent(data.invoice_id)}`);
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      // dialog already closed on success; keep open if error
    }
  };

  const handleKitsChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setKits(!isNaN(value) && value > 0 ? value : 1);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '--';
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value || '');
  const isValidPhone = (value) => {
    const digits = (value || '').replace(/\D/g, '');
    return digits.length >= 10;
  };

  const shippingAddress = useMemo(() => {
    const p = selectedPractice || {};
    const list = Array.isArray(p.addresses) ? p.addresses : [];
    const fromList =
      list.find(a => a?.purpose === 'shipping') ||
      list.find(a => a?.purpose === 'billing') ||
      list[0];
    const candidate = fromList || {
      street_1: p.shipping_street_1,
      street_2: p.shipping_street_2,
      city: p.shipping_city,
      state: p.shipping_state,
      zip_code: p.shipping_zip_code,
    };
    if (!candidate || !candidate.street_1) return null;
    const line1 = [candidate.street_1, candidate.street_2].filter(Boolean).join(', ');
    const line2 = [candidate.city, candidate.state].filter(Boolean).join(', ');
    const line3 = [line2, candidate.zip_code].filter(Boolean).join(' ');
    return {
      line1,
      line2: line3 || null,
    };
  }, [selectedPractice]);

  const practiceAddresses = useMemo(() => {
    const p = selectedPractice || {};
    const list = Array.isArray(p.addresses) ? p.addresses : [];
    if (list.length > 0) return list;
    if (!p.shipping_street_1) return [];
    return [{
      street_1: p.shipping_street_1,
      street_2: p.shipping_street_2,
      city: p.shipping_city,
      state: p.shipping_state,
      zip_code: p.shipping_zip_code,
      purpose: 'shipping',
    }];
  }, [selectedPractice]);

  const handleSelectPracticeAddress = (addr, idx) => {
    const key = addr?.id || addr?.address_id || `${idx}`;
    setSelectedPracticeAddressKey(key);
    setAlternatePayerAddress({
      name: selectedPractice?.name || '',
      street_1: addr?.street_1 || addr?.address_1 || '',
      street_2: addr?.street_2 || addr?.address_2 || '',
      city: addr?.city || '',
      state: addr?.state || '',
      zip_code: addr?.zip_code || addr?.postal_code || '',
    });
  };

  const canProceedFromContact = alternatePayerMethod === 'email'
    ? isValidEmail(alternatePayerEmail)
    : isValidPhone(alternatePayerPhone);

  const handleSendAlternatePayerInvoice = async () => {
    setAlternatePayerSending(true);
    setAlternatePayerError(null);
    try {
      const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
      if (!selectedPractice?.id) {
        throw new Error('No practice selected');
      }
      const res = await fetch(`${apiBase}/qb/purchase-kits/alternate-payer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.id_token}`,
        },
        body: JSON.stringify({
          quantity: kits,
          practice_id: selectedPractice.id,
          invoice_delivery_method: alternatePayerMethod,
          email: alternatePayerMethod === 'email' ? (alternatePayerEmail || null) : null,
          sms_phone_number: alternatePayerMethod === 'qr_code' ? (alternatePayerPhone || null) : null,
          payer_name: alternatePayerAddress.name,
          shipping_name: alternatePayerAddress.name,
          shipping_address_1: alternatePayerAddress.street_1,
          shipping_address_2: alternatePayerAddress.street_2,
          shipping_city: alternatePayerAddress.city,
          shipping_state: alternatePayerAddress.state,
          shipping_zip_code: alternatePayerAddress.zip_code,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Error ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      if (alternatePayerMethod === 'qr_code') {
        let invoiceUrl = '';
        try {
          const linkRes = await fetch(
            `${apiBase}/qb/invoices/${encodeURIComponent(data.realm_id)}/${encodeURIComponent(data.invoice_id)}/payment-link`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${tokens.id_token}`,
              },
            }
          );
          if (!linkRes.ok) {
            const text = await linkRes.text();
            throw new Error(text || `Error ${linkRes.status}: ${linkRes.statusText}`);
          }
          const linkData = await linkRes.json();
          if (linkData?.invoice_url) {
            invoiceUrl = linkData.invoice_url;
          } else {
            throw new Error('QuickBooks invoice URL was not returned.');
          }
        } catch (linkErr) {
          console.error('Error fetching QuickBooks invoice URL:', linkErr);
          setAlternatePayerError('Invoice created, but QuickBooks hosted URL could not be loaded.');
        }
        setAlternatePayerInvoiceUrl(invoiceUrl);
        setAlternatePayerStep('qr_code');
      } else {
        handleAlternatePayerDialogClose();
        navigate(`/transactions?realm_id=${encodeURIComponent(data.realm_id)}&invoice_id=${encodeURIComponent(data.invoice_id)}`);
      }
    } catch (err) {
      console.error('Error creating alternate payer invoice:', err);
      setAlternatePayerError(err.message);
    } finally {
      setAlternatePayerSending(false);
    }
  };

  const canProceedFromShipping = Boolean(
    alternatePayerAddress.name &&
    alternatePayerAddress.street_1 &&
    alternatePayerAddress.city &&
    alternatePayerAddress.state &&
    alternatePayerAddress.zip_code
  );

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom>
        Purchase Kits
      </Typography>
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        V1
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>VeraBIND™ Tau</strong> is a first-in-class, blood-based assay that quantifies disease-driving hyperphosphorylated tau activity, giving clinicians an affordable, non-invasive tool for the early detection and diagnosis of Alzheimer’s disease; verified against tau-PET imaging with over 96 % overall agreement for confidence you can trust.
      </Typography>
      <FormControl component="fieldset" sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Who is Paying?
        </Typography>
        <RadioGroup
          row
          name="payer"
          value={payer}
          onChange={(e) => setPayer(e.target.value)}
          sx={{ mt: 1 }}
        >
          <FormControlLabel value="practice" control={<Radio />} label="Practice" />
          <FormControlLabel value="patient" control={<Radio />} label="Individual Payer" />
        </RadioGroup>
      </FormControl>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>
        Select Number of Kits
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
        <TextField
          label="Number of Kits"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 1 } }}
          value={kits}
          onChange={handleKitsChange}
        />
        <Button variant="contained" color="primary" onClick={handleOrderClick}>
          {payer === 'patient' ? 'Continue with Individual Payer' : 'Continue to Checkout'}
        </Button>
      </Box>
      <Box sx={{ mt: 2, mb: 2 }}>
        {quoteLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="textSecondary">
              Loading current pricing...
            </Typography>
          </Box>
        ) : (
          <>
            {quote && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Pricing summary
                </Typography>
                <Typography variant="body2">
                  Subtotal ({quote.quantity} kit{quote.quantity > 1 ? 's' : ''}): {formatCurrency(quote.subtotal)}
                </Typography>
                <Typography variant="body2">
                  Discount: -{formatCurrency(quote.discount)}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  Total: {formatCurrency(quote.total)}
                </Typography>
              </>
            )}
            {quoteError && (
              <Alert severity="error" sx={{ mt: quote ? 1 : 0 }}>
                Failed to load pricing: {quoteError}
              </Alert>
            )}
          </>
        )}
      </Box>
      <Dialog open={dialogOpen} onClose={handleCancel}>
        <DialogTitle>Confirm Purchase Kits</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to order {kits} kit{kits > 1 ? 's' : ''}. An invoice will be created and sent to your email address, {user?.email}. Items will be shipped according to your purchase agreement.
          </DialogContentText>
          <Divider sx={{ my: 2 }} />
          {quoteLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="textSecondary">
                Fetching current price...
              </Typography>
            </Box>
          ) : quote ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {Array.isArray(quote.line_items) && quote.line_items.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Line items
                  </Typography>
                  {quote.line_items.map((item, idx) => (
                    <Box key={`${item.sku}-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        {item.name || `SKU ${item.sku}`} × {quote.quantity}
                      </Typography>
                      <Typography variant="body2">{formatCurrency(item.line_subtotal)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="body2">
                Subtotal: {formatCurrency(quote.subtotal)}
              </Typography>
              <Typography variant="body2">
                Discount: -{formatCurrency(quote.discount)}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Total due: {formatCurrency(quote.total)}
              </Typography>
            </Box>
          ) : (
            <Alert severity="info">Pricing not available. Please try again.</Alert>
          )}
          {shippingAddress ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Shipping to
              </Typography>
              <Typography variant="body2">{shippingAddress.line1}</Typography>
              {shippingAddress.line2 && <Typography variant="body2">{shippingAddress.line2}</Typography>}
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No shipping address on file for this practice.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateInvoice}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={alternatePayerDialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleAlternatePayerDialogClose();
        }}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown
      >
        {!alternatePayerQrFullscreen && (
          <DialogTitle>{alternatePayerStep === 'qr_code' ? 'Invoice QR Code' : 'Alternate Payer'}</DialogTitle>
        )}
        <DialogContent>
          {alternatePayerStep === 'contact' && (
            <>
              <DialogContentText>
                Choose how the individual payer should receive the invoice.
              </DialogContentText>
              <Box sx={{ mt: 2, display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' } }}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <RadioGroup
                    name="alternate-payer-method"
                    value={alternatePayerMethod}
                    onChange={(e) => setAlternatePayerMethod(e.target.value)}
                    sx={{ gap: 2 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormControlLabel
                        value="email"
                        control={<Radio />}
                        label="Receive Invoice by Email"
                      />
                      <TextField
                        label="Email"
                        size="small"
                        type="email"
                        value={alternatePayerEmail}
                        onChange={(e) => setAlternatePayerEmail(e.target.value)}
                        disabled={alternatePayerMethod !== 'email'}
                        error={alternatePayerMethod === 'email' && alternatePayerEmail.length > 0 && !isValidEmail(alternatePayerEmail)}
                        helperText={
                          alternatePayerMethod === 'email' && alternatePayerEmail.length > 0 && !isValidEmail(alternatePayerEmail)
                            ? 'Enter a valid email address.'
                            : ' '
                        }
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormControlLabel
                        value="qr_code"
                        control={<Radio />}
                        label="Recieve Invoice by QR Code"
                      />
                      <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          A QR code will be provided to present the invoice to the payer.
                        </Typography>
                        <TextField
                          label="Payer Phone Number"
                          size="small"
                          type="tel"
                          value={alternatePayerPhone}
                          onChange={(e) => setAlternatePayerPhone(e.target.value)}
                          disabled={alternatePayerMethod !== 'qr_code'}
                          error={alternatePayerMethod === 'qr_code' && alternatePayerPhone.length > 0 && !isValidPhone(alternatePayerPhone)}
                          helperText={
                            alternatePayerMethod === 'qr_code' && alternatePayerPhone.length > 0 && !isValidPhone(alternatePayerPhone)
                              ? 'Enter a valid phone number.'
                              : ' '
                          }
                          fullWidth
                        />
                        <Typography variant="caption" color="textSecondary">
                          We only use this phone number if there is a payment issue. It is not used for invoice delivery.
                        </Typography>
                      </Box>
                    </Box>
                  </RadioGroup>
                </FormControl>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: 'grey.50',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    What Happens Next
                  </Typography>
                  {alternatePayerMethod === 'email' ? (
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Enter the payer&apos;s email address.
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Next, choose where the kit(s) should be shipped.
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Review the order details, then the invoice will be sent to the provided email.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Continue to shipping details for the kit order.
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Review the order details and create the invoice.
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        A QR code will then be displayed so the payer can access the invoice.
                      </Typography>
                    </>
                  )}
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Kits ship after the invoice is paid.
                  </Typography>
                </Box>
              </Box>
            </>
          )}
          {alternatePayerStep === 'shipping' && (
            <>
              <DialogContentText>
                Select where the kits should be shipped.
              </DialogContentText>
              <Box sx={{ mt: 2, display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle2">Practice Addresses</Typography>
                  {practiceAddresses.length === 0 ? (
                    <Alert severity="info">No addresses on file for this practice.</Alert>
                  ) : (
                    practiceAddresses.map((addr, idx) => {
                      const key = addr?.id || addr?.address_id || `${idx}`;
                      const line1 = [addr?.street_1 || addr?.address_1, addr?.street_2 || addr?.address_2]
                        .filter(Boolean)
                        .join(', ');
                      const line2 = [addr?.city, addr?.state].filter(Boolean).join(', ');
                      const line3 = [line2, addr?.zip_code || addr?.postal_code].filter(Boolean).join(' ');
                      return (
                        <Box
                          key={key}
                          onClick={() => handleSelectPracticeAddress(addr, idx)}
                          sx={{
                            border: '1px solid',
                            borderColor: selectedPracticeAddressKey === key ? 'primary.main' : 'grey.300',
                            borderRadius: 1,
                            p: 1.5,
                            cursor: 'pointer',
                            backgroundColor: selectedPracticeAddressKey === key ? 'action.selected' : 'background.paper',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {(addr?.purpose || 'Address').toString().replace(/_/g, ' ')}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {line1 || '—'}
                          </Typography>
                          {line3 && (
                            <Typography variant="body2" color="textSecondary">
                              {line3}
                            </Typography>
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle2">Shipping Address</Typography>
                  <TextField
                    label="Name"
                    size="small"
                    value={alternatePayerAddress.name}
                    onChange={(e) => setAlternatePayerAddress(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                  <TextField
                    label="Address 1"
                    size="small"
                    value={alternatePayerAddress.street_1}
                    onChange={(e) => setAlternatePayerAddress(prev => ({ ...prev, street_1: e.target.value }))}
                    required
                  />
                  <TextField
                    label="Address 2"
                    size="small"
                    value={alternatePayerAddress.street_2}
                    onChange={(e) => setAlternatePayerAddress(prev => ({ ...prev, street_2: e.target.value }))}
                    placeholder="Optional"
                  />
                  <TextField
                    label="City"
                    size="small"
                    value={alternatePayerAddress.city}
                    onChange={(e) => setAlternatePayerAddress(prev => ({ ...prev, city: e.target.value }))}
                    required
                  />
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField
                      label="State"
                      size="small"
                      value={alternatePayerAddress.state}
                      onChange={(e) => setAlternatePayerAddress(prev => ({ ...prev, state: e.target.value }))}
                      required
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Zip Code"
                      size="small"
                      value={alternatePayerAddress.zip_code}
                      onChange={(e) => setAlternatePayerAddress(prev => ({ ...prev, zip_code: e.target.value }))}
                      required
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Box>
              </Box>
            </>
          )}
          {alternatePayerStep === 'summary' && (
            <>
              <DialogContentText>
                Review the order, payer, and shipping details before continuing.
              </DialogContentText>
              {alternatePayerSending && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="textSecondary">
                    Sending invoice...
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  mt: 2,
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
                }}
              >
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: 'grey.50',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Items & Price
                  </Typography>
                  {quote ? (
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {Array.isArray(quote.line_items) && quote.line_items.length > 0 ? (
                        quote.line_items.map((item, idx) => (
                          <Box key={`${item.sku}-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                              {item.name || `SKU ${item.sku}`} × {quote.quantity}
                            </Typography>
                            <Typography variant="body2">{formatCurrency(item.line_subtotal)}</Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          {quote.quantity} kit{quote.quantity > 1 ? 's' : ''} selected.
                        </Typography>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Subtotal</Typography>
                        <Typography variant="body2">{formatCurrency(quote.subtotal)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Discount</Typography>
                        <Typography variant="body2">-{formatCurrency(quote.discount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Total
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {formatCurrency(quote.total)}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1.5 }}>
                      Pricing not available.
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Invoice Delivery
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {alternatePayerMethod === 'email'
                        ? `Email: ${alternatePayerEmail}`
                        : 'QR Code (display after invoice creation)'}
                    </Typography>
                  </Box>
                  {alternatePayerMethod === 'qr_code' && (
                    <Alert severity="info">
                      Reminder: after invoice creation, we will display a QR code the payer can scan to pay.
                    </Alert>
                  )}
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Shipping Address
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                      {alternatePayerAddress.name}
                    </Typography>
                    <Typography variant="body2">
                      {[alternatePayerAddress.street_1, alternatePayerAddress.street_2].filter(Boolean).join(', ')}
                    </Typography>
                    <Typography variant="body2">
                      {[alternatePayerAddress.city, alternatePayerAddress.state].filter(Boolean).join(', ')}{' '}
                      {alternatePayerAddress.zip_code}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
          {alternatePayerStep === 'qr_code' && (
            <>
              {!alternatePayerQrFullscreen && (
                <DialogContentText>
                  Invoice created. Present this QR code to the payer.
                </DialogContentText>
              )}
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: alternatePayerQrFullscreen ? '80vh' : 360,
                  gap: 2,
                }}
              >
                <Box
                  ref={qrFullscreenRef}
                  sx={{
                    position: alternatePayerQrFullscreen ? 'fixed' : 'relative',
                    inset: alternatePayerQrFullscreen ? 0 : 'auto',
                    zIndex: alternatePayerQrFullscreen ? 9999 : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: alternatePayerQrFullscreen ? '100vw' : 'auto',
                    height: alternatePayerQrFullscreen ? '100vh' : 'auto',
                    backgroundColor: alternatePayerQrFullscreen ? '#000' : 'transparent',
                    gap: 2,
                  }}
                >
                  {alternatePayerQrFullscreen && (
                    <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                      <IconButton
                        aria-label="Close full screen QR view"
                        onClick={() => {
                          if (document.fullscreenElement) {
                            document.exitFullscreen().catch(() => {});
                          } else {
                            setAlternatePayerQrFullscreen(false);
                          }
                        }}
                        sx={{ backgroundColor: 'rgba(255,255,255,0.85)' }}
                      >
                        X
                      </IconButton>
                    </Box>
                  )}
                  {alternatePayerInvoiceUrl ? (
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(alternatePayerInvoiceUrl)}`}
                      alt="Invoice QR Code"
                      style={{ width: 320, height: 320 }}
                    />
                  ) : (
                    <Alert severity="error">Invoice URL is unavailable for QR rendering.</Alert>
                  )}
                  {alternatePayerQrFullscreen && alternatePayerInvoiceUrl && (
                    <Typography variant="body2" sx={{ color: '#fff', textAlign: 'center', px: 2 }}>
                      Hold this screen up for the payer to scan. Keep bystanders away for privacy.
                    </Typography>
                  )}
                </Box>
                {!alternatePayerQrFullscreen && alternatePayerInvoiceUrl && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Scan to open the invoice URL.
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
                      {alternatePayerInvoiceUrl}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
          {alternatePayerError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {alternatePayerError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', display: alternatePayerQrFullscreen ? 'none' : 'flex' }}>
          <Button onClick={handleAlternatePayerDialogClose} disabled={alternatePayerSending}>
            {alternatePayerStep === 'qr_code' ? 'OK' : 'Cancel'}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {alternatePayerStep !== 'contact' && alternatePayerStep !== 'qr_code' && (
              <Button
                onClick={() => setAlternatePayerStep(alternatePayerStep === 'summary' ? 'shipping' : 'contact')}
                disabled={alternatePayerSending}
              >
                Back
              </Button>
            )}
            {alternatePayerStep === 'qr_code' ? (
              <Button
                variant="contained"
                onClick={async () => {
                  if (!qrFullscreenRef.current) return;
                  try {
                    if (qrFullscreenRef.current.requestFullscreen) {
                      await qrFullscreenRef.current.requestFullscreen();
                    }
                    setAlternatePayerQrFullscreen(true);
                  } catch (err) {
                    console.error('Failed to enter full screen QR view:', err);
                  }
                }}
                disabled={!alternatePayerInvoiceUrl}
              >
                Full Screen
              </Button>
            ) : alternatePayerStep === 'summary' ? (
              <Button variant="contained" onClick={handleSendAlternatePayerInvoice} disabled={alternatePayerSending}>
                {alternatePayerSending
                  ? 'Creating...'
                  : (alternatePayerMethod === 'qr_code' ? 'Create Invoice and Display QR Code' : 'Send the Invoice to Payer')}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => setAlternatePayerStep(alternatePayerStep === 'contact' ? 'shipping' : 'summary')}
                disabled={alternatePayerSending || (alternatePayerStep === 'contact' ? !canProceedFromContact : !canProceedFromShipping)}
              >
                Next
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
      {error && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {/* Navigation takes user to Transactions on success */}
    </Container>
  );
}
